'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { BackupScheduleForm, type BackupScheduleFormRef } from '@/components/partials/backup-schedules'
import { useCreateBackupSchedule } from '@/hooks'
import { type CreateBackupScheduleParams } from '@/types/backup'
import { FormModeEnum } from '@/enums'

const BackupScheduleCreate = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const formRef = useRef<BackupScheduleFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createBackupSchedule, isPending } = useCreateBackupSchedule()

  const handleSubmit = (data: CreateBackupScheduleParams) => {
    createBackupSchedule(data, {
      onSuccess: async newBackup => {
        setIsRedirecting(true)
        queryClient.setQueryData(['backup-schedule', newBackup.id], newBackup)
        await queryClient.invalidateQueries({ queryKey: ['backup-schedules'] })
        toast.success(t('created_successfully'))
        router.push(`/backups/schedules/${newBackup.id}`)
      },
      onError: (error: any) => {
        if (error.response?.status === 422 || error.isValidationError) {
          formRef.current?.handleValidationErrors(error)
          return
        }

        toast.error(t('error_creating'))
      }
    })
  }

  const handleSubmitAndNew = (data: CreateBackupScheduleParams) => {
    createBackupSchedule(data, {
      onSuccess: async newBackup => {
        queryClient.setQueryData(['backup-schedule', newBackup.id], newBackup)
        await queryClient.invalidateQueries({ queryKey: ['backup-schedules'] })
        const detailUrl = `/backups/schedules/${newBackup.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        formRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: (error: any) => {
        if (error.response?.status === 422 || error.isValidationError) {
          formRef.current?.handleValidationErrors(error)
          return
        }

        toast.error(t('error_creating'))
      }
    })
  }

  return (
    <BackupScheduleForm
      key={formKey}
      ref={formRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={() => router.push('/backups/schedules')}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default BackupScheduleCreate
